<template>
  <Modal :value="value" @input="cancel" width="lg">
    <div slot="header">
      Edição em lote
    </div>

    <div class="container">
      <!-- AITs list -->
      <div class="mb-4">
        <h5>AITs sendo editadas:</h5>
        <span v-for="ticket in tickets" :key="ticket.id" class="badge badge-primary mr-1 mb-1">
          {{ ticket.ait }}
        </span>
      </div>

      <hr>

      <!-- Status and Messages Section -->
      <div class="px-2 mb-3">
        <div class="form-group">
          <div class="d-flex align-items-center justify-content-between">
            <label for="batch-status">Status da multa</label>
            <div class="field-actions">
              <label>
                <span class="mr-2">Não alterar</span>
                <input type="checkbox" v-model="statusNotChange">
              </label>
            </div>
          </div>

          <div v-if="statusNotChange" class="input-group">
            <input class="form-control" disabled value="Não alterar" />
          </div>

          <select v-else id="batch-status" class="form-control" v-model="status">
            <option value="0">Sem informação</option>
            <option value="100">Em aberto</option>
            <option value="200">Vencido</option>
            <option value="300">Prescrito</option>
            <option value="500">Pago</option>
            <option value="600">Cancelado</option>
          </select>
        </div>

        <div class="form-group">
          <label>
            <input type="checkbox" v-model="removeAlerts" class="mr-2">
            Remover alertas
          </label>
        </div>
      </div>

      <hr>

      <!-- Custom fields -->
      <div class="custom-fields-section px-2">
        <h5>Campos personalizados</h5>
        <div v-if="customFields.length === 0" class="text-muted">
          Nenhum campo personalizado configurado
        </div>

        <div v-for="(field, index) in customFields" :key="field.name" class="form-group">
          <div class="d-flex align-items-center justify-content-between">
            <label :for="field.name">{{ field.name }}</label>
            <div class="field-actions">
              <label class="mr-3">
                <span class="mr-2">Não alterar</span>
                <input type="checkbox" ref="notChange" :checked="showAction(field.name, NOT_CHANGE)"
                  @click="toggleFieldAction(index, field.name, NOT_CHANGE)">
              </label>
              <label>
                <span class="mr-2">Remover</span>
                <input type="checkbox" ref="toRemove" :checked="showAction(field.name, TO_REMOVE)"
                  @click="toggleFieldAction(index, field.name, TO_REMOVE)">
              </label>
            </div>
          </div>

          <template v-if="showEditField(field.name)">
            <div v-if="showAction(field.name, TO_REMOVE)" class="input-group">
              <input class="form-control" disabled value="A remover" />
            </div>

            <div v-else-if="showAction(field.name, NOT_CHANGE)" class="input-group">
              <input class="form-control" disabled value="Não alterar" />
            </div>

            <template v-else>
              <div v-if="field.kind !== 'select'" class="input-group">
                <input :id="field.name" type="text" class="form-control" v-model="map[field.name].value"
                  :placeholder="`Digite o valor para ${field.name}`">
              </div>

              <div v-else class="input-group">
                <select :id="field.name" class="form-control" v-model="map[field.name].value">
                  <option v-for="option in field.values" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>
            </template>
          </template>

        </div>
      </div>
    </div>

    <div slot="footer">
      <button class="btn btn-outline mr-2" @click="cancel">Cancelar</button>
      <button class="btn btn-primary" @click="save" :disabled="saving">
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </button>
    </div>
  </Modal>
</template>

<script>
import Modal from '../shares/specialTag/Modal'
import GlobalLoading from '@/mixins/GlobalLoading'

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

export default {
  name: 'EditBatch',
  components: { Modal },
  beforeMount() {
    this.initializeMap()
  },
  mixins: [GlobalLoading],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    tickets: {
      type: Array,
      default: () => []
    },
    customFields: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      saving: false,
      status: '0',
      statusNotChange: true,
      removeAlerts: false,
      map: {},
      TO_REMOVE: 'toRemove',
      TO_UPDATE: 'toUpdate',
      NOT_CHANGE: 'notChange',
    }
  },
  methods: {
    initializeMap() {
      this.customFields.forEach(field => this.$set(this.map, field.name, { value: '', action: this.NOT_CHANGE }))
    },
    cancel() {
      this.$emit('input', false)
    },
    toggleFieldAction(index, fieldName, action) {
      if (this.map[fieldName].action === action) {
        this.map[fieldName].action = this.TO_UPDATE
      } else {
        this.map[fieldName].action = action
        const targetAction = action === this.TO_REMOVE ? 'notChange' : 'toRemove'
        this.uncheckElement(targetAction, index)
      }
    },
    uncheckElement(targetAction, index) {
      this.$refs[targetAction][index].checked = false
    },
    showAction(fieldName, action) {
      return this.map[fieldName].action === action
    },
    showEditField(fieldName) {
      return this.map[fieldName]
    },
    saved() {
      this.$emit('input', false)
      this.$emit('ticketsSaved')
    },
    async save() {
      if (this.saving) return
      if (!(confirm(`Deseja aplicar alterações em ${this.tickets.length} Multa(s)`))) return

      this.saving = true
      this.openGlobalLoad()
      const customFields = {}
      Object.entries(this.map).forEach(([key, map]) => {
        if (map.action === this.TO_REMOVE)
          customFields[key] = null
        else if (map.action === this.TO_UPDATE)
          customFields[key] = map.value
      })

      const params = { custom_fields: customFields }

      if (!this.statusNotChange) {
        params.status = this.status
      }

      if (this.removeAlerts) {
        params.remove_alerts = true
      }

      for (const ticket of this.tickets) {
        await sleep(300)
        await this.updateTicket(ticket, params)
      }

      this.saving = false
      this.closeGlobalLoad()
      this.saved()
      this.initializeMap()
      this.status = '0'
      this.statusNotChange = true
      this.removeAlerts = false
    },
    async updateTicket(ticket, params) {
      try {
        await this.axios.v3.put(`/cm-embarcador/multas/${ticket.id}`, params)
        this.$notify.add(`Multa ${ticket.ait} atualizada com sucesso!`)
      } catch (e) {
        this.$notify.addError(e)
      }
    }
  },
  watch: {
    customFields() {
      this.initializeMap()
    }
  }
}
</script>

<style scoped>
.field-actions {
  margin-left: 10px;
}

.custom-fields-section {
  max-height: 400px;
  overflow-y: auto;
}

.badge {
  font-size: 0.75em;
}

.input-group {
  margin-bottom: 0;
}
</style>
